import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { AboutProductsBanner } from '~/src/components/blocks/agency-products/about-products-banner/about-products-banner';
import { DocumentsBlock } from '~/src/components/blocks/agency-products/documents-block/documents-block';
import { OtherDirectionProducts } from '~/src/components/blocks/agency-products/other-direction-products/other-direction-products';
import { RecommendProductBanner } from '~/src/components/blocks/agency-products/recommend-product-banner/recommend-product-banner';
import { ABOUT_PRODUCTS } from '~/src/params';
import { NavLink } from '~/src/components/common-components/nav-link/nav-link';
import { useMatchMedia } from '~/src/hooks/use-match-media.hook';
import { mtsEditorDist } from '@developers/mts-editor-dist';
import { NoSSR } from '~/src/helpers/noSSR.helper';
import { MediaWidthEnum } from '~/src/models/enums/media-width.enum';
import { CounterpartyConnectStatusEnum } from '~/src/models/enums/counterparty-connect-status.enum';
import { COLORS } from '~/src/styles/tokens/colors';
import { ReferralAgentLinkEnum } from '~/src/models/enums/referral-agent-link.enum';
import { Notification } from '~/src/components/common-components/notification/notification';
import { setStateReferralAgentLinkAction } from '~/src/redux/referral-agent/referral-agent.slice';
import {
    selectAgentId,
    selectCurrentOrganizationConnectionStatus,
    selectIsAuthFinished,
    selectWorkingPartyId,
} from '~/src/redux/user/user.selectors';
import { selectIsAuth } from '~/src/redux/id-token/id-token.selectors';
import { selectStateReferralAgentLink } from '~/src/redux/referral-agent/referral-agent.selector';
import { setAgentIdAction } from '~/src/redux/user/user.slice';
import { SvgArrowIcon } from '~/src/components/svg-components/arrow-icon';
import type { AboutProductInterface } from '~/src/models/types/about-product.types';
import * as Styled from './about-products-layout.styles';

interface ReferralProductsLayoutProps {
    productData: AboutProductInterface;
}

const AboutProductsLayout: FC<ReferralProductsLayoutProps> = ({ productData }) => {
    const router = useRouter();
    const dispatch = useDispatch();
    const { content, documents, productGroupName, productList, sefUrl, leadCreation, productName } = productData;
    const [isShowDocumentsBlock, setIsShowDocumentsBlock] = useState(false);
    const [isMobile] = useMatchMedia(MediaWidthEnum.mobile, { withoutSSR: true });
    const isAuthFinished = useSelector(selectIsAuthFinished);
    const isAuth = useSelector(selectIsAuth);
    const stateReferralAgentLink = useSelector(selectStateReferralAgentLink);
    const organizationConnectionStatus = useSelector(selectCurrentOrganizationConnectionStatus);
    const agentId = useSelector(selectAgentId);
    const workingPartyId = useSelector(selectWorkingPartyId);
    const isConnectedStatus = organizationConnectionStatus === CounterpartyConnectStatusEnum.CONNECTED;
    const isNotShowRecommendProductBanner = isAuthFinished && isAuth && isConnectedStatus && leadCreation !== 'true';

    const hideNotificationDelay = 5000;

    useEffect(() => {
        agentId.length && dispatch(setAgentIdAction(''));
    }, [workingPartyId]);

    useEffect(() => {
        mtsEditorDist.destroy();
        mtsEditorDist.init();
        return () => {
            mtsEditorDist.destroy();
        };
    }, [productData]);

    useEffect(() => {
        if (stateReferralAgentLink !== ReferralAgentLinkEnum.default) {
            setTimeout(() => {
                dispatch(setStateReferralAgentLinkAction(ReferralAgentLinkEnum.default));
            }, hideNotificationDelay);
        }
    }, [stateReferralAgentLink]);

    useEffect(() => {
        dispatch(setStateReferralAgentLinkAction(ReferralAgentLinkEnum.default));
    }, [productName]);

    useEffect(() => {
        if (documents?.value?.length) {
            setIsShowDocumentsBlock(true);
        } else {
            setIsShowDocumentsBlock(false);
        }
    }, [documents]);

    useEffect(() => {
        delete router?.query?.id;
    }, []);

    const handleGoBack = () => {
        router.push({ pathname: `${ABOUT_PRODUCTS}` }).then();
    };

    return (
        <>
            <NoSSR>
                {!isMobile && (
                    <Styled.SectionContainerGoBack>
                        <Styled.LinkWrapperGoBack>
                            <Styled.SvgWrapper onClick={handleGoBack}>
                                <SvgArrowIcon />
                            </Styled.SvgWrapper>
                            <NavLink
                                linkColor={COLORS.textLightPrimaryLink}
                                href={ABOUT_PRODUCTS}
                                textDecoration={'underline'}
                            >
                                Назад
                            </NavLink>
                        </Styled.LinkWrapperGoBack>
                    </Styled.SectionContainerGoBack>
                )}
            </NoSSR>
            <Styled.ContainerLayout>
                {stateReferralAgentLink !== ReferralAgentLinkEnum.default && (
                    <Notification state={stateReferralAgentLink} top={100} widthError={348} widthSuccess={302} />
                )}
                <Styled.SectionContainerAbout isShowDocumentsBlock={isShowDocumentsBlock}>
                    <Styled.ContentContainerAboutProductBanner>
                        <AboutProductsBanner
                            productData={productData}
                            isAuth={isAuth}
                            isAuthFinished={isAuthFinished}
                            isConnectedStatus={isConnectedStatus}
                        />
                    </Styled.ContentContainerAboutProductBanner>
                </Styled.SectionContainerAbout>
                {isShowDocumentsBlock && (
                    <Styled.SectionContainerDocumentsBlock>
                        <DocumentsBlock documents={documents} />
                    </Styled.SectionContainerDocumentsBlock>
                )}
                <Styled.SectionContainerCMSContent isNotShowRecommendProductBanner={isNotShowRecommendProductBanner}>
                    <Styled.DescriptionProductTitle>Описание продукта</Styled.DescriptionProductTitle>
                    <Styled.ContentContainerCMSContent>
                        {content && <div id="mts-editor" dangerouslySetInnerHTML={{ __html: `${content}` }} />}
                    </Styled.ContentContainerCMSContent>
                </Styled.SectionContainerCMSContent>
                {!isNotShowRecommendProductBanner && (
                    <Styled.SectionContainerRecommendProductBanner>
                        <RecommendProductBanner
                            productData={productData}
                            isAuth={isAuth}
                            isAuthFinished={isAuthFinished}
                            isConnectedStatus={isConnectedStatus}
                        />
                    </Styled.SectionContainerRecommendProductBanner>
                )}
                {productList?.length > 1 && (
                    <Styled.SectionContainerOtherDirectionProducts>
                        <OtherDirectionProducts
                            productGroupName={productGroupName}
                            productList={productList.filter((v) => v.sefUrl !== sefUrl)}
                        />
                    </Styled.SectionContainerOtherDirectionProducts>
                )}
                <Styled.SectionContainerNavigation>
                    <Styled.LinkWrapper>
                        <Styled.SvgWrapper onClick={handleGoBack}>
                            <SvgArrowIcon />
                        </Styled.SvgWrapper>
                        <NavLink
                            linkColor={COLORS.textLightPrimaryLink}
                            href={ABOUT_PRODUCTS}
                            textDecoration={'underline'}
                        >
                            На страницу агентских продуктов
                        </NavLink>
                    </Styled.LinkWrapper>
                </Styled.SectionContainerNavigation>
            </Styled.ContainerLayout>
        </>
    );
};

export default AboutProductsLayout;
